import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Pencil, Check } from 'lucide-react';
import { dispatchFetch, T, F, bd } from '@/lib/dispatchApi';

const SKILLS = [
  { id: 'photo', label: 'Photo' },
  { id: 'drone', label: 'Drone' },
  { id: 'reel', label: 'Reel' },
  { id: 'tour', label: 'Tour' },
  { id: 'twilight', label: 'Twilight' },
];

export default function DispatchProfileEditPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', phone: '', bio: '', skills: ['photo'], service_areas: '', home_city: '' });
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const fetchProfile = useCallback(async () => {
    try {
      const res = await dispatchFetch('/api/profile');
      if (!res.ok) throw new Error();
      const data = await res.json();
      setProfile(data);
      setForm({
        name: data.name ?? '',
        phone: data.phone ?? '',
        bio: data.bio ?? '',
        skills: Array.isArray(data.skills) && data.skills.length ? data.skills : ['photo'],
        service_areas: (data.service_areas || []).join(', '),
        home_city: data.home_city ?? '',
      });
    } catch {
      // empty form
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await dispatchFetch('/api/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          phone: form.phone,
          bio: form.bio,
          skills: form.skills,
          service_areas: form.service_areas.split(',').map((s) => s.trim()).filter(Boolean),
          home_city: form.home_city,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSaved(true);
      setTimeout(() => navigate(-1), 800);
    } catch (err) {
      setError('Could not save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const onRpas = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('level', 'advanced');
      const res = await dispatchFetch('/api/profile/rpas', { method: 'POST', body: fd });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setProfile(data);
    } catch (err) {
      setError('Could not upload Advanced RPAS. Try again.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const toggleSkill = (id) => {
    setForm((f) => ({
      ...f,
      skills: f.skills.includes(id) ? f.skills.filter((s) => s !== id) : [...f.skills, id],
    }));
  };

  const Field = ({ label, field, placeholder, multiline = false }) => (
    <div style={{ marginBottom: 20 }}>
      <p style={{ fontFamily: F.heading, fontSize: 13, fontWeight: 500, color: T.moonFaint, letterSpacing: 0, marginBottom: 8 }}>
        {label}
      </p>
      {multiline ? (
        <textarea
          value={form[field]}
          onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
          placeholder={placeholder}
          rows={4}
          style={{
            width: '100%', boxSizing: 'border-box',
            backgroundColor: T.nightSoft, ...bd(T.nightHair), borderRadius: 2,
            padding: '12px 14px', fontSize: 14, color: T.moon,
            fontFamily: F.heading, resize: 'vertical',
            outline: 'none', lineHeight: 1.5,
          }}
          onFocus={e => (e.target.style.borderColor = T.nightHairS)}
          onBlur={e => (e.target.style.borderColor = T.nightHair)}
        />
      ) : (
        <input
          type={field === 'phone' ? 'tel' : 'text'}
          value={form[field]}
          onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
          placeholder={placeholder}
          style={{
            width: '100%', boxSizing: 'border-box',
            backgroundColor: T.nightSoft, ...bd(T.nightHair), borderRadius: 2,
            padding: '12px 14px', fontSize: 14, color: T.moon,
            fontFamily: F.heading, outline: 'none',
          }}
          onFocus={e => (e.target.style.borderColor = T.nightHairS)}
          onBlur={e => (e.target.style.borderColor = T.nightHair)}
        />
      )}
    </div>
  );

  return (
    <div style={{ backgroundColor: T.night, minHeight: '100vh', color: T.moon, fontFamily: F.heading }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 20px 14px', borderBottom: `1px solid ${T.nightHair}` }}>
        <button
          onClick={() => navigate(-1)}
          aria-label="Back"
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 44, minHeight: 44, margin: '-13px 0 -13px -13px' }}
        >
          <ArrowLeft size={18} color={T.moonMute} strokeWidth={1.5} />
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
          <Pencil size={15} color={T.moonMute} strokeWidth={1.5} />
          <span style={{ fontSize: 15, fontWeight: 500, color: T.moon }}>Edit profile</span>
        </div>
        <button
          onClick={handleSave}
          disabled={saving || saved || loading}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            backgroundColor: saved ? T.ok + '33' : T.brick,
            ...bd(saved ? T.ok + '66' : T.brickDeep), borderRadius: 2,
            padding: '8px 14px', minHeight: 44, cursor: saving || saved ? 'default' : 'pointer',
            opacity: loading ? 0.4 : 1,
          }}
        >
          {saved
            ? <><Check size={13} color={T.okText} strokeWidth={2} /><span style={{ fontSize: 13, color: T.okText }}>Saved</span></>
            : <span style={{ fontSize: 13, color: saving ? T.moonMute : '#FFF' }}>{saving ? 'Saving…' : 'Save'}</span>
          }
        </button>
      </div>

      {loading ? (
        <div style={{ padding: '48px 20px', textAlign: 'center' }}>
          <p style={{ color: T.moonFaint, fontSize: 13 }}>Loading…</p>
        </div>
      ) : (
        <div style={{ padding: '24px 20px' }}>
          {error && (
            <div style={{ backgroundColor: T.bad + '20', ...bd(T.bad + '40'), borderRadius: 2, padding: '12px 14px', marginBottom: 20 }}>
              <p style={{ fontSize: 13, color: T.badText, margin: 0 }}>{error}</p>
            </div>
          )}
          {profile && (
            <div style={{ marginBottom: 24, padding: '12px 14px', backgroundColor: T.nightSoft, ...bd(T.nightHair), borderRadius: 2 }}>
              <p style={{ fontSize: 13, color: T.moonFaint, margin: 0 }}>
                {[profile.title || profile.role, (profile.tier_label || profile.tier) ? `Tier ${profile.tier_label || profile.tier}` : null].filter(Boolean).join(' · ')}
              </p>
              <p style={{ fontSize: 13, color: T.moon, margin: '6px 0 0' }}>{profile.payout_label || (profile.payout_model === 'owner_full' ? 'Full package · $0 deduction' : 'Fixed rate card')}</p>
            </div>
          )}
          <Field label="Full name" field="name" placeholder="Your name" />
          <Field label="Phone" field="phone" placeholder="+1 (416) 000-0000" />
          <Field label="Home city" field="home_city" placeholder="Toronto" />
          <Field label="Service area" field="service_areas" placeholder="Toronto, Mississauga, Vaughan" />
          <Field label="Bio" field="bio" placeholder="A short note about your experience and specialty" multiline />

          <div style={{ marginBottom: 20 }}>
            <p style={{ fontSize: 13, fontWeight: 500, color: T.moonFaint, marginBottom: 8 }}>Skills</p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {SKILLS.map((s) => {
                const on = form.skills.includes(s.id);
                return (
                  <button
                    key={s.id}
                    type="button"
                    aria-pressed={on}
                    onClick={() => toggleSkill(s.id)}
                    style={{
                      // Selected is a state, not an action: off-white, so Save stays the one orange button.
                      background: on ? T.moon : T.nightSoft,
                      color: on ? T.night : T.moon,
                      ...bd(on ? T.moon : T.nightHair),
                      borderRadius: 2, padding: '8px 12px', minHeight: 44, cursor: 'pointer',
                      fontFamily: F.heading, fontSize: 13,
                    }}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <p style={{ fontSize: 13, fontWeight: 500, color: T.moonFaint, marginBottom: 8 }}>Advanced RPAS</p>
            {profile?.rpas_certificate_url ? (
              <a href={profile.rpas_certificate_url} target="_blank" rel="noreferrer" style={{ color: T.attnText, fontSize: 13, display: 'block', marginBottom: 10 }}>
                Certificate on file
              </a>
            ) : (
              <p style={{ fontSize: 13, color: T.moonFaint, margin: '0 0 10px' }}>No certificate on file.</p>
            )}
            <input ref={fileRef} type="file" accept=".pdf,image/*" onChange={onRpas} disabled={uploading} style={{ minHeight: 44, color: T.moonMute, fontSize: 13 }} />
            {uploading && <p style={{ fontSize: 12, color: T.moonFaint, marginTop: 8 }}>Uploading…</p>}
          </div>
        </div>
      )}
    </div>
  );
}
